'use client';

import { ArrowLeft, CheckCircle2, CreditCard } from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';

import { TotemCheckoutSummary } from '@/app/(users)/totem-by-slug/[slug]/components/checkout/totem-checkout-summary';
import { TotemPaymentMethodPicker } from '@/app/(users)/totem-by-slug/[slug]/components/checkout/totem-payment-method-picker';
import { useTotemCheckout } from '@/app/(users)/totem-by-slug/[slug]/hooks/use-totem-checkout';
import { useTotem } from '@/app/(users)/totem-by-slug/[slug]/providers/totem-provider';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@/components/ui/dialog';
import { Spinner } from '@/components/ui/spinner';
import type { PaymentMethod } from '@/interface/payment-method';

export function TotemCheckoutDialog() {
  const { checkoutOpen, cart } = useTotem();
  const {
    step,
    availableMethods,
    qrData,
    qrMode,
    amountTendered,
    setAmountTendered,
    handleCashPayment,
    handleQrPayment,
    handleCardPayment,
    handleMpLinkPayment,
    handleClose,
    handleBack,
    isPending,
    cashNumberStr,
  } = useTotemCheckout();

  const handleMethodSelect = (method: PaymentMethod) => {
    if (method.type === 'efectivo') {
      handleCashPayment(method);
    } else if (method.type === 'qr') {
      handleQrPayment(method);
    } else if (method.type === 'mercado_pago') {
      handleMpLinkPayment(method);
    } else {
      handleCardPayment(method);
    }
  };

  return (
    <Dialog open={checkoutOpen} onOpenChange={open => !open && handleClose()}>
      <DialogContent
        className="flex flex-col max-h-[95vh] w-[95vw] max-w-[95vw] sm:max-w-[95vw] md:max-w-6xl lg:max-w-7xl p-0 gap-0 border-0 bg-background/95 backdrop-blur-md overflow-hidden"
        showCloseButton={false}
      >
        <DialogTitle className="sr-only">Cobrar pedido</DialogTitle>
        <DialogDescription className="sr-only">Seleccione un método de pago y confirme el pedido.</DialogDescription>

        <div className="flex flex-col h-full min-h-[60vh] flex-1">
          {/* Custom Header */}
          <div className="flex shrink-0 items-center gap-4 px-6 py-4 border-b bg-background">
            <button
              type="button"
              onClick={handleBack}
              className="flex size-10 items-center justify-center rounded-full bg-muted/50 hover:bg-muted transition-colors"
            >
              <ArrowLeft className="size-5" />
            </button>
            <h2 className="text-xl font-bold">
              {step === 'payment' && 'Cobrar pedido'}
              {step === 'processing' && 'Procesando...'}
              {step === 'waiting_terminal' && 'Pago Tarjeta'}
              {step === 'qr_payment' && 'Pago QR'}
              {step === 'cashier' && 'Pago exitoso'}
            </h2>
          </div>

          {step === 'payment' && (
            <div className="grid grid-cols-1 md:grid-cols-12 flex-1 min-h-0 divide-y md:divide-y-0 md:divide-x bg-muted/10 overflow-hidden">
              {/* Left Column: Payment Methods */}
              <div className="col-span-1 md:col-span-7 lg:col-span-8 p-6 md:p-8 lg:p-10 flex flex-col gap-8 overflow-y-auto">
                <div className="flex-1 bg-background rounded-2xl border p-6 md:p-8 shadow-sm">
                  <TotemPaymentMethodPicker
                    isPending={isPending}
                    methods={availableMethods}
                    onSelect={handleMethodSelect}
                    cartTotal={cart.total}
                    amountTendered={amountTendered}
                    onAmountTenderedChange={setAmountTendered}
                  />
                </div>
              </div>

              {/* Right Column: Summary */}
              <div className="col-span-1 md:col-span-5 lg:col-span-4 p-6 md:p-8 bg-background flex flex-col min-h-0">
                <div className="flex-1 min-h-0 mb-6 flex flex-col">
                  <TotemCheckoutSummary />
                </div>
              </div>
            </div>
          )}

          {step === 'processing' && (
            <div className="flex flex-col items-center justify-center flex-1 py-20 gap-6">
              <Spinner className="size-12" />
              <p className="text-xl font-medium">Estamos creando tu pedido...</p>
            </div>
          )}

          {step === 'waiting_terminal' && (
            <div className="flex flex-col items-center justify-center flex-1 py-12 gap-8 text-center px-4">
              <div>
                <h3 className="text-3xl font-bold mb-2">Pagá con tu tarjeta!</h3>
                <p className="text-muted-foreground text-lg">Una vez que el pago sea exitoso, tu pedido se procesará</p>
              </div>

              <div className="relative py-8 flex justify-center items-center">
                <div className="absolute size-48 bg-orange-500/10 rounded-full animate-ping" />
                <div className="absolute size-32 bg-orange-500/20 rounded-full animate-pulse" />
                <div className="relative bg-white p-6 rounded-2xl shadow-xl border rotate-[-10deg] transform hover:rotate-0 transition-transform">
                  <CreditCard className="size-20 text-slate-800" />
                </div>
              </div>

              <h4 className="text-xl font-bold max-w-sm mt-4">
                Acercá tu tarjeta a la terminal para completar el pago!
              </h4>

              <div className="mt-8">
                <p className="text-sm text-muted-foreground">
                  Para cancelar el pago, presioná el botón de cancelar en el lector.
                </p>
              </div>
            </div>
          )}

          {step === 'cashier' && (
            <div className="flex flex-col items-center justify-center flex-1 py-20 gap-6 text-center px-4">
              <CheckCircle2 className="size-24 text-emerald-500" />
              <h3 className="text-3xl font-bold">Pedido generado con éxito</h3>
              {cashNumberStr && <div className="text-6xl font-black text-primary my-4">{cashNumberStr}</div>}
              <p className="text-lg text-muted-foreground max-w-sm">
                Aboná este pedido en la caja para que pase a preparación.
              </p>
            </div>
          )}

          {step === 'qr_payment' && (
            <div className="flex flex-col items-center justify-center flex-1 gap-8 px-4 py-12 text-center">
              <div>
                <h3 className="mb-2 text-2xl font-bold">
                  {qrMode === 'static' ? 'Escaneá el QR fijo del local' : 'Escaneá el código QR'}
                </h3>
                {qrMode === 'static' ? (
                  <p className="text-muted-foreground text-sm">
                    Usá el código QR impreso de este punto de venta para completar el pago.
                  </p>
                ) : null}
              </div>

              {qrMode === 'static' ? (
                <div className="bg-muted/20 flex size-64 items-center justify-center rounded-2xl border border-dashed p-6">
                  <p className="text-muted-foreground text-sm">Esperando escaneo del QR estático…</p>
                </div>
              ) : qrData ? (
                <div className="rounded-2xl border-4 border-muted bg-white p-6">
                  <QRCodeSVG value={qrData} size={256} />
                </div>
              ) : (
                <div className="bg-muted/20 flex size-64 animate-pulse items-center justify-center rounded-2xl border">
                  <span className="text-muted-foreground">Generando código...</span>
                </div>
              )}

              <div className="mx-auto flex w-full max-w-xs items-center justify-between rounded-full border border-orange-200 bg-orange-50 px-6 py-3 text-orange-600 shadow-sm dark:border-orange-800/50 dark:bg-orange-900/30 dark:text-orange-400">
                <Spinner className="size-5 shrink-0 bg-orange-600 dark:bg-orange-400" />
                <span className="font-medium">Esperando pago...</span>
                <div className="size-5 shrink-0" />
              </div>
            </div>
          )}

          {step === 'mp_redirect' && (
            <div className="flex flex-col items-center justify-center flex-1 py-20 gap-6 text-center px-4">
              <Spinner className="size-12" />
              <h3 className="text-3xl font-bold">Redirigiendo a Mercado Pago...</h3>
              <p className="text-lg text-muted-foreground max-w-sm">
                Serás redirigido de forma segura para completar tu pago.
              </p>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
